import { ChangeEvent, memo, useCallback, useEffect, useState } from "react";
import {
  Card,
  Input,
  message,
  notification,
  Select,
  Table,
  Button as IconButton,
} from "antd";
import { DeleteOutlined, EditOutlined, PlusOutlined } from "@ant-design/icons";
import { ERoles, IPond } from "pages/Ponds/types";
import { useMsal } from "@azure/msal-react";
import { changeApplications, loadedApplications } from "http/userApi";
import { generateApplication, modalError } from "helpers";
import { applicationColumns } from "const";
import { Applications, ApplicationsToDisplay } from "./types";
import { v4 as uuidv4, validate as isGuid } from "uuid";
import Button from "common/Button";
import Tooltip from "common/Tooltip";
import { useLogin } from "hooks/useLogin";

import { FolderTooltip } from "../Tooltips/FolderTooltip";
import { AccessLevelTooltip } from "../Tooltips/AccessLevelTooltip";
import { AzureApplicationIdTooltip } from "../Tooltips/AzureApplicationIdTooltip";
import { AzureADApplicationsTooltip } from "../Tooltips/AzureADApplicationsTooltip";

import "./style.scss";

const AzureApplication = ({
  bucket,
  onFetchBucket,
}: {
  bucket: IPond;
  onFetchBucket: () => Promise<unknown>;
}) => {
  const msalInstance = useMsal();
  const { showAuthorizationErrorModal } = useLogin(msalInstance);

  const [edit, setEdit] = useState<string>("");
  const [loading, setLoading] = useState(false);
  const [savedApplications, setSavedApplications] = useState<{
    [index: string]: { level: keyof typeof ERoles; prefix: "" }[];
  }>();

  const [applicationsCollection, setApplicationsCollection] = useState<
    ApplicationsToDisplay[]
  >([]);

  useEffect(() => {
    const getApplications = async () => {
      setLoading(true);

      const result: {
        data: Applications | null;
        error: null | string;
      } = await loadedApplications(bucket.name, msalInstance);

      if (result.data && !result.error) {
        setSavedApplications(result.data);
        setApplicationsCollection(generateApplication(result.data));
      }

      setLoading(false);
    };

    getApplications();
  }, [bucket.name]);

  const switchToEdit = useCallback((block: string) => setEdit(block), []);

  const editCancel = useCallback(() => {
    if (savedApplications) {
      setApplicationsCollection(generateApplication(savedApplications));
    }
    setEdit("");
  }, [savedApplications]);

  const handleAddSpecifyFolders = (itemKey: number) => {
    setApplicationsCollection(prevApplicationsCollection =>
      prevApplicationsCollection.map(applicationItem =>
        applicationItem.key === itemKey
          ? {
              ...applicationItem,
              type: {
                ...applicationItem.type,
                value: [
                  ...applicationItem.type.value,
                  {
                    level: "ro",
                    prefix: "",
                    prefixKey: uuidv4(),
                  },
                ],
              },
            }
          : applicationItem,
      ),
    );
  };

  const handleAddApplication = useCallback(
    () =>
      applicationsCollection.length
        ? setApplicationsCollection(prevApplicationsCollection => [
            ...prevApplicationsCollection,
            {
              key:
                prevApplicationsCollection[
                  prevApplicationsCollection.length - 1
                ].key + 1,
              applicationId: {
                name: "",
                itemKey:
                  prevApplicationsCollection[
                    prevApplicationsCollection.length - 1
                  ].applicationId.itemKey + 1,
              },
              select: {
                value: "all",
                itemKey:
                  prevApplicationsCollection[
                    prevApplicationsCollection.length - 1
                  ].applicationId.itemKey + 1,
              },
              type: {
                value: [
                  {
                    level: "ro",
                    prefix: "",
                    prefixKey: uuidv4(),
                  },
                ],
                itemKey:
                  prevApplicationsCollection[
                    prevApplicationsCollection.length - 1
                  ].type.itemKey + 1,
              },
            },
          ])
        : setApplicationsCollection([
            {
              key: 0,
              applicationId: {
                name: "",
                itemKey: 0,
              },
              select: {
                value: "all",
                itemKey: 0,
              },
              type: {
                value: [
                  {
                    level: "ro",
                    prefix: "",
                    prefixKey: uuidv4(),
                  },
                ],
                itemKey: 0,
              },
            },
          ]),
    [applicationsCollection],
  );

  const saveApplications = async () => {
    setLoading(true);

    for (let i = 0; i < applicationsCollection.length; i++) {
      if (applicationsCollection[i].applicationId.name === "") {
        setLoading(false);
        return notification.error({
          message:
            'The "Application (client) ID" field is required. Make sure they are filled in.',
        });
      }

      if (!isGuid(applicationsCollection[i].applicationId.name)) {
        setLoading(false);
        return notification.error({
          message: "Some of Application (client) ID has not UUID format.",
        });
      }

      if (
        applicationsCollection[i].select.value === "specify" &&
        applicationsCollection[i].type.value.length === 1 &&
        applicationsCollection[i].type.value[0].prefix === ""
      ) {
        setLoading(false);
        return notification.error({
          message: 'The "Specify folders" field must not be empty',
        });
      }

      if (
        applicationsCollection[i] &&
        applicationsCollection[i + 1] &&
        applicationsCollection[i].applicationId.name ===
          applicationsCollection[i + 1].applicationId.name
      ) {
        setLoading(false);
        return notification.error({
          message: "The Application (client) ID must be unique",
        });
      }
    }

    const applications: {
      [index: string]: {
        level: keyof typeof ERoles;
      };
    } = {};

    applicationsCollection.forEach(item => {
      Object.assign(applications, {
        [item.applicationId.name]: item.type.value.map(
          ({ prefixKey, ...rest }) => rest,
        ),
      });
    });

    const result: {
      data: {
        [index: string]: {
          level: keyof typeof ERoles;
          prefix: string;
        }[];
      } | null;
      error: null | string;
    } = await changeApplications(bucket.name, applications, msalInstance);

    if (result.error === "token expired") {
      return showAuthorizationErrorModal();
    }

    if (result.error === "Can't delete the last owner") {
      notification.error({
        message: "Error",
        description: result.error,
        duration: 3,
      });
      setLoading(false);
      setEdit("");
      return;
    }

    if (result.data && !result.error) {
      await onFetchBucket();
      message.success({
        content: "Applications are updated",
        duration: 2,
      });
      setEdit("");
    } else {
      modalError(result.error);
    }

    setLoading(false);
  };

  const handleChangeApplicationId = (
    inputValue: string,
    field: string,
    item: { name: string; itemKey: number },
  ) => {
    setApplicationsCollection(prevApplicationsCollection =>
      prevApplicationsCollection.map(applicationItem =>
        applicationItem.key === item.itemKey
          ? {
              ...applicationItem,
              [field]: { itemKey: item.itemKey, name: inputValue },
            }
          : applicationItem,
      ),
    );
  };

  const handleChangePrefixInput = useCallback(
    (event: ChangeEvent<HTMLInputElement>, key: number) => {
      setApplicationsCollection(prevApplicationsCollection =>
        prevApplicationsCollection.map(applicationItem =>
          applicationItem.key === key
            ? {
                ...applicationItem,
                type: {
                  ...applicationItem.type,
                  value: applicationItem.type.value.map(item =>
                    item.prefixKey === event.target.name
                      ? {
                          ...item,
                          prefix: event.target.value,
                        }
                      : item,
                  ),
                },
              }
            : applicationItem,
        ),
      );
    },
    [applicationsCollection],
  );

  const handleChangeFoldersSelect = useCallback(
    (value: string, itemKey: number) => {
      setApplicationsCollection(prevApplicationsCollection =>
        prevApplicationsCollection.map(applicationItem =>
          applicationItem.key === itemKey
            ? {
                ...applicationItem,
                select: { value: value, itemKey },
                type: {
                  ...applicationItem.type,
                  value:
                    value === "all"
                      ? [
                          {
                            level: "ro",
                            prefix: "",
                            prefixKey: uuidv4(),
                          },
                        ]
                      : applicationItem.type.value,
                },
              }
            : applicationItem,
        ),
      );
    },
    [applicationsCollection],
  );

  const handleDelete = useCallback(
    (
      applicationKey: number,
      prefixKey: string,
      found: {
        level: "ro" | "owner" | "rw";
        prefix: string;
        prefixKey?: string | undefined;
      }[],
    ) => {
      if (found.length === 1) {
        setApplicationsCollection(prevApplicationsCollection =>
          prevApplicationsCollection.filter(
            applicationItem => applicationItem.key !== applicationKey,
          ),
        );

        return;
      }

      setApplicationsCollection(prevApplicationsCollection =>
        prevApplicationsCollection.map(applicationItem =>
          applicationItem.key === applicationKey
            ? {
                ...applicationItem,
                type: {
                  ...applicationItem.type,
                  value: applicationItem.type.value.filter(
                    item => item.prefixKey !== prefixKey,
                  ),
                },
              }
            : applicationItem,
        ),
      );
    },
    [applicationsCollection],
  );

  const handleChangePrefixSelect = useCallback(
    (key: number, value: keyof typeof ERoles, prefixKey: string) => {
      setApplicationsCollection(prevApplicationsCollection =>
        prevApplicationsCollection.map(applicationItem =>
          applicationItem.key === key
            ? {
                ...applicationItem,
                type: {
                  ...applicationItem.type,
                  value: applicationItem.type.value.map(item =>
                    item.prefixKey === prefixKey
                      ? {
                          ...item,
                          level: value,
                        }
                      : item,
                  ),
                },
              }
            : applicationItem,
        ),
      );
    },
    [applicationsCollection],
  );

  const editableApplicationColumns = [
    {
      title: () => (
        <Tooltip
          title="Application (client) ID"
          tooltip={<AzureApplicationIdTooltip />}
        />
      ),
      dataIndex: "applicationId",
      render: (record: { name: string; itemKey: number }) => {
        return (
          <Input
            defaultValue={record.name}
            onChange={e =>
              handleChangeApplicationId(e.target.value, "applicationId", record)
            }
          />
        );
      },
      key: "applicationId",
      width: "35%",
    },
    {
      title: () => (
        <Tooltip title="Folders access mode" tooltip={<FolderTooltip />} />
      ),
      dataIndex: "select",
      render: (record: { value: string; itemKey: number }) => {
        return (
          <Select
            className="column-folders-select"
            value={record.value}
            onChange={(value: string) =>
              handleChangeFoldersSelect(value, record.itemKey)
            }
          >
            <Select.Option value="all">All folders</Select.Option>
            <Select.Option value="specify">Specify folders</Select.Option>
          </Select>
        );
      },
      key: "select",
      width: "10%",
    },
    {
      title: () => (
        <div className="access-titles">
          <div>Pond folders</div>
          <Tooltip title="Access level" tooltip={<AccessLevelTooltip />} />
          <div>Action</div>
        </div>
      ),
      dataIndex: "select",
      render: (record: { value: string; itemKey: number }) => {
        const foundItem = applicationsCollection.find(
          item => item.key === record.itemKey,
        );

        return (
          <>
            {record.value === "all" ? (
              <div className="access-fields-wrapper">
                <Input
                  name="all"
                  onChange={e => handleChangePrefixInput(e, record.itemKey)}
                  value={foundItem?.type.value[0].prefix}
                  disabled={record.value === "all"}
                />
                <Select
                  className="item-selector"
                  onChange={value =>
                    handleChangePrefixSelect(
                      record.itemKey,
                      value,
                      foundItem?.type.value[0].prefixKey
                        ? foundItem.type.value[0].prefixKey
                        : "",
                    )
                  }
                  value={foundItem?.type.value[0].level}
                >
                  <Select.Option value="owner">Owner</Select.Option>
                  <Select.Option value="ro">Read only</Select.Option>
                  <Select.Option value="rw">Read write</Select.Option>
                </Select>
                <div style={{ maxWidth: "fit-content" }}>
                  <IconButton
                    danger
                    shape="circle"
                    icon={<DeleteOutlined />}
                    onClick={() =>
                      handleDelete(
                        record.itemKey,
                        foundItem?.type.value[0].prefixKey
                          ? foundItem.type.value[0].prefixKey
                          : "",
                        foundItem?.type.value ? foundItem?.type.value : [],
                      )
                    }
                  />
                </div>
              </div>
            ) : (
              foundItem?.type.value.map(item => (
                <div className="access-fields-wrapper">
                  <Input
                    name={item.prefixKey}
                    onChange={e => handleChangePrefixInput(e, record.itemKey)}
                    value={item.prefix}
                    disabled={record.value === "all"}
                  />
                  <Select
                    className="item-selector"
                    onChange={value =>
                      handleChangePrefixSelect(
                        record.itemKey,
                        value,
                        item.prefixKey ? item.prefixKey : "",
                      )
                    }
                    value={item.level}
                  >
                    <Select.Option value="owner">Owner</Select.Option>
                    <Select.Option value="ro">Read only</Select.Option>
                    <Select.Option value="rw">Read write</Select.Option>
                  </Select>
                  <div style={{ maxWidth: "fit-content" }}>
                    <IconButton
                      danger
                      shape="circle"
                      icon={<DeleteOutlined />}
                      onClick={() =>
                        handleDelete(
                          record.itemKey,
                          item.prefixKey ? item.prefixKey : "",
                          foundItem.type.value,
                        )
                      }
                    />
                  </div>
                </div>
              ))
            )}
            {record.value === "all" ? null : (
              <Button
                style={{
                  width: "100%",
                  marginTop: "8px",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
                size="middle"
                type="default"
                onClick={() => handleAddSpecifyFolders(record.itemKey)}
                icon={<PlusOutlined />}
              >
                Add
              </Button>
            )}
          </>
        );
      },
      key: "pondFolders",
      width: "55%",
    },
  ];

  return (
    <div className="access-pond access-group">
      <Card
        title={
          <Tooltip
            title="AzureAD Applications"
            tooltip={<AzureADApplicationsTooltip />}
          />
        }
        className="access-pond-section"
        extra={
          edit !== "applications" ? (
            <Button
              type="primary"
              size="large"
              onClick={() => switchToEdit("applications")}
              icon={<EditOutlined />}
              disabled={!bucket.management_permissions.can_add_applications}
            >
              Edit
            </Button>
          ) : (
            <div className="edit-buttons">
              <Button className="cancel-btn" onClick={editCancel} size="large">
                Cancel
              </Button>
              <Button
                htmlType="submit"
                disabled={loading}
                type="primary"
                size="large"
                onClick={saveApplications}
              >
                Save
              </Button>
            </div>
          )
        }
      >
        {edit === "applications" ? (
          <>
            <Table
              dataSource={applicationsCollection}
              columns={editableApplicationColumns}
              loading={loading}
              pagination={false}
            />
            <Button
              type="dashed"
              disabled={loading}
              className="add-permissions-btn"
              icon={<PlusOutlined />}
              onClick={handleAddApplication}
            >
              Add application
            </Button>
          </>
        ) : bucket.management_permissions.can_add_applications ? (
          <Table
            columns={applicationColumns}
            dataSource={applicationsCollection}
            pagination={false}
            loading={loading}
          />
        ) : (
          <div className="disabled-block">
            <Table
              columns={applicationColumns}
              dataSource={applicationsCollection}
              pagination={false}
              loading={loading}
            />
          </div>
        )}
      </Card>
    </div>
  );
};

export default memo(AzureApplication);
